import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Box, Dialog, DialogActions, DialogContent, Grid } from '@mui/material';
import { isArray } from 'lodash';
import { useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useRecoilValue } from 'recoil';
import { IndividualService } from '../../../../Component/Api/Individual/IndividualService';
import AppCheckbox from '../../../../Component/Common/Components/FormValidationInputs/AppCheckbox';
import { AppDatepicker } from '../../../../Component/Common/Components/FormValidationInputs/AppDatepicker';
import AppSelectList, { IDropDownOptions } from '../../../../Component/Common/Components/FormValidationInputs/AppSelectList';
import AppTextInput from '../../../../Component/Common/Components/FormValidationInputs/AppTextInput';
import { EEOccupationalLevelList } from '../../../../Component/Common/Constants/Lists/DropdownListOptions';
import { EE123PeopleRole_Edit, PeopleEditRole } from '../../../../Component/Common/Constants/UserRoles';
import { Individual } from '../../../../Component/Model/Dto/Individual';
import { userRolesState } from '../../../../State/user.Atom';
import { CustomDialogTitle } from '../../Custom/CustomDialogTitle';
import { employmentDataSchema } from './EmploymentDataValidation';

interface IEmploymentModalProps {
    id: number;
    individualId: number;
    showEmploymentDataModal: boolean;
    closeEmploymentModal: () => void;
    onSaved: (id: number) => void;
    hasEmployeeDataEntries: boolean;
}

const EmployeeLevelList: IDropDownOptions[] = [
    { value: "Employee", id: 1 },
    { value: "Employee Previously Unemployed", id: 4 }
]

export const EmploymentDataModal = (props: IEmploymentModalProps) => {

    const [id, setId] = useState<number>(props.id);
    const [startDateTime, setStartDateTime] = useState<Date | undefined>(undefined);
    const [endDateTime, setEndDateTime] = useState<Date | undefined>(undefined);
    const userRoles = useRecoilValue(userRolesState);
    const { control, reset, handleSubmit, formState: { isSubmitting }, setValue } = useForm({
        mode: 'all',
        resolver: yupResolver(employmentDataSchema)
    });

    const editRoleAccess = [
        EE123PeopleRole_Edit
    ]
    const isEditable = userRoles.some(r => editRoleAccess.includes(r));

    const getDataFromApi = async () => {
        const result = await IndividualService.GetIndividualEmploymentDataById(id, props.individualId);

        // set these values to be used for date validation, otherwise the values are undefined
        // and the user will be able to select a start date that is after the end date and vice versa
        setStartDateTime(result.data.StartDateDateTime);
        setEndDateTime(result.data.EndDateDateTime);

        reset(result.data);
    }

    const handleDataSubmit = async (data: FieldValues) => {
        try {
            const finalData = data as Individual.SaveEmploymentDataDto;
            if (id === 0) {
                finalData.EndDateSetByUser = false;
                finalData.EndDateTimeRequired = false;
                finalData.IndividualEmployeeDataId = 0;
                finalData.IsTechnicalPosition = false;
                finalData.StartDateSetByUser = props.hasEmployeeDataEntries;
                finalData.Stipend = 0;
                finalData.UseStartDateValidation = true;
                finalData.IndividualId = props.individualId;
                finalData.Active = true;
            }
            const result = await IndividualService.SaveIndividualEmploymentData(finalData);
            toast.success('Employment Data Saved Successfully');
            setId(result.data);
            setValue('IndividualEmployeeDataId', result.data);
            props.onSaved(result.data);
            props.closeEmploymentModal()

        } catch (error: any) {
            if (isArray(error.response.data)) {
                let Errors: string[] = error.response.data;
                Errors.forEach((error: string) => {
                    toast.error("Overlapping dates for: " + error);
                });
            }
        }
    }

    useEffect(() => {
        if (props.id !== 0) {
            getDataFromApi();
        }
        else {
            setValue('IsPermanent', true);
        }

        setValue('hasEntires', props.hasEmployeeDataEntries);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.id])

    return (
        <Dialog disableEnforceFocus open={props.showEmploymentDataModal} onClose={() => { props.closeEmploymentModal() }} aria-labelledby="form-dialog-title" maxWidth='lg'>
            <CustomDialogTitle id="customized-dialog-title" onClose={() => props.closeEmploymentModal()}>
                {props.id === 0 ? 'Capture new Employment Data' : isEditable ? 'Edit Employment Data' : 'View Employment Data'}
            </CustomDialogTitle>
            <DialogContent>
                <Box>
                    <form id='EmploymentDataForm' onSubmit={handleSubmit(handleDataSubmit)}>
                        <Grid container spacing={1}>
                            <Grid item xs={12} sm={12} mt={1}>
                                <AppSelectList control={control} name='OccupationalLevel' label='Occupational Level' items={EEOccupationalLevelList} disabled={!isEditable} />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <AppSelectList control={control} name='EmployeeType' label='Employment Type' items={EmployeeLevelList} disabled={!isEditable} />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <AppTextInput control={control} name='Position' label='Position' disabled={!isEditable} />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <AppDatepicker control={control} name='StartDateDateTime' label='Start Date' inputFormat='yyyy/MM/dd' maxDate={endDateTime} onChange={setStartDateTime} disabled={!isEditable} />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <AppDatepicker control={control} name='EndDateDateTime' label='End Date' inputFormat='yyyy/MM/dd' minDate={startDateTime} onChange={setEndDateTime} disabled={!isEditable} />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <AppTextInput control={control} name='OfoCode' label='OFO code' disabled={!isEditable} />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <AppCheckbox control={control} name='IsPermanent' label='Permanent Employment' disabled={!isEditable} />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <AppCheckbox control={control} name='TrainingReceived' label='Received Training?' disabled={!isEditable} />
                            </Grid>
                        </Grid>
                    </form>
                </Box>
            </DialogContent>
            <DialogActions>
                {isEditable &&
                    <LoadingButton loading={isSubmitting} variant='contained' type="submit" form='EmploymentDataForm' color='primary' disabled={!isEditable} >
                        Save
                    </LoadingButton>
                }
            </DialogActions>
        </Dialog>
    )
}

